import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* ─── Variants ─────────────────────────────────────────────────────────────── */

const progressTrackVariants = cva('bg-raios-elevated rounded-full w-full', {
  variants: {
    size: {
      sm: 'h-1.5',
      default: 'h-2.5',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const progressFillVariants = cva(
  'rounded-full transition-[width] duration-500 ease-out h-full',
  {
    variants: {
      variant: {
        default: 'bg-raios-accent',
        success: 'bg-raios-success',
        warning: 'bg-raios-warning',
        danger: 'bg-raios-danger',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

/* ─── Shimmer keyframe style ──────────────────────────────────────────────── */

const shimmerKeyframes = `
@keyframes progressShimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
}
`;

/* ─── Types ────────────────────────────────────────────────────────────────── */

interface ProgressBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof progressFillVariants>,
    VariantProps<typeof progressTrackVariants> {
  value?: number;
  indeterminate?: boolean;
  glow?: boolean;
  label?: string;
}

/* ─── Glow map ─────────────────────────────────────────────────────────────── */

const glowMap: Record<string, string> = {
  default: 'glow-blue',
  success: 'glow-success',
  warning: 'glow-amber',
  danger: 'glow-danger',
};

/* ─── ProgressBar ──────────────────────────────────────────────────────────── */

const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value = 0, variant = 'default', size, glow = false, label, indeterminate = false, className, ...props }, ref) => {
    const clampedValue = Math.min(100, Math.max(0, value));
    const resolvedVariant = variant ?? 'default';

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        {indeterminate && <style>{shimmerKeyframes}</style>}
        {label && (
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-raios-muted">{label}</span>
            {!indeterminate && (
              <span className="text-xs font-medium tabular-nums text-raios-text-secondary">
                {Math.round(clampedValue)}%
              </span>
            )}
          </div>
        )}
        <div
          role="progressbar"
          aria-valuenow={indeterminate ? undefined : clampedValue}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label ?? 'Progress'}
          className={cn(progressTrackVariants({ size }), 'overflow-hidden')}
        >
          {indeterminate ? (
            <div
              className="h-full w-[30%] rounded-full bg-raios-accent"
              style={{
                animation: 'progressShimmer 1.5s ease-in-out infinite',
              }}
            />
          ) : (
            <div
              className={cn(
                progressFillVariants({ variant }),
                glow && glowMap[resolvedVariant]
              )}
              style={{ width: `${clampedValue}%` }}
            />
          )}
        </div>
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

// eslint-disable-next-line react-refresh/only-export-components
export { ProgressBar, progressFillVariants, progressTrackVariants };
export type { ProgressBarProps };
